import { EarningsItem } from '@api/api/earnings';
import { Card } from '@design/components/card/Card';
import { Separator } from '@design/components/separator/Separator';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { Markdown } from '@terminal/components/markdown/Markdown';
import { THEME_TAG_COLOR_CLASS_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import { useThemeEarningsSummary } from '@terminal/react-query-hooks/basket-summary/useThemeEarningsSummary';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import clsx from 'clsx';
import { format } from 'date-fns';
import { Fragment, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { getCalendarQuarterInfo } from '../../earnings/utils/utils';
import type { EarningsTopicClickHandler } from '../earnings-reporting-summary/types';
import { useThemeEarningsDrivers } from '../useThemeEarningsDrivers';
import {
  type CalendarQuarterPeriod,
  type EarningsPresentationVariant,
  getEarningsCitationDrawerParams,
  getRecapBasisCount,
  linkSummaryCitations,
} from '../utils';
import styles from './EarningsReadThrough.module.scss';

export interface EarningsReadThroughProps {
  themeId: string;
  reportedEarnings: EarningsItem[];
  activeReportingPeriod?: CalendarQuarterPeriod;
  onTopicClick: EarningsTopicClickHandler;
  variant?: EarningsPresentationVariant;
}

export const EarningsReadThrough = ({
  themeId,
  reportedEarnings,
  activeReportingPeriod,
  onTopicClick,
  variant = 'page',
}: EarningsReadThroughProps) => {
  const { t } = useTranslation(['widget']);
  const { openDrawer } = useEarningsInsightDrawer();
  const isDrawer = variant === 'drawer';
  const cardSize = isDrawer ? 'small' : 'large';
  const cardClassName = clsx(styles.root, {
    [styles.drawer]: isDrawer,
  });
  const reportingPeriodRange = activeReportingPeriod
    ? getCalendarQuarterInfo(new Date(activeReportingPeriod.reporting_date))
    : undefined;
  const { data: summary, isLoading: isSummaryLoading } =
    useThemeEarningsSummary({
      themeId,
      from: reportingPeriodRange?.startDate.toISOString(),
      to: reportingPeriodRange?.endDate.toISOString(),
    });
  const { drivers, isFetching: isDriversFetching } =
    useThemeEarningsDrivers(reportedEarnings);

  const content = summary?.content;
  const hasReporters = reportedEarnings.length > 0;
  const hasDrivers = drivers.length > 0;
  const narrative = content
    ? linkSummaryCitations(content.summary, content.citations)
    : undefined;
  const recapBasisCount = content ? getRecapBasisCount(content) : 0;

  const handleSummaryClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const href = event.target.closest('a')?.getAttribute('href');
    const drawerParams = getEarningsCitationDrawerParams(href ?? null);
    if (!drawerParams) {
      return;
    }

    event.preventDefault();
    openDrawer({
      ...drawerParams,
      subtab: SubTabsType.Recap,
      source: isDrawer
        ? POSTHOG_TRACKING_SOURCES.THEME_EARNINGS_DRAWER
        : POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
    });
  };

  // Switching quarters changes the query key, so `isLoading` covers both the
  // first load and a period switch without flashing the previous quarter.
  // Drivers refetch every time another company reports, which must not blank a
  // narrative that is already on screen.
  if (isSummaryLoading || (isDriversFetching && !narrative)) {
    return (
      <Card
        size={cardSize}
        className={cardClassName}
        data-testid="earnings-read-through-loading"
      >
        <SkeletonLoader areas={[{ className: styles.skeletonArea }]} />
      </Card>
    );
  }

  if (!narrative && !hasDrivers) {
    return null;
  }

  return (
    <Card
      size={cardSize}
      className={cardClassName}
      data-testid="earnings-read-through"
    >
      {narrative && (
        <>
          <div className={styles.summarySection}>
            <div className={styles.header}>
              <p className={styles.title}>
                {t('widget:themeOverview.tabs.earnings.readThrough.title')}
              </p>
              {summary?.updatedAt && (
                <span className={styles.lastUpdated}>
                  {t('widget:themeOverview.pulse.lastUpdatedAt', {
                    date: format(new Date(summary.updatedAt), 'h:mm a, MMM d'),
                  })}
                </span>
              )}
            </div>
            <div className={styles.summary} onClick={handleSummaryClick}>
              <Markdown message={narrative} />
            </div>
            {recapBasisCount > 0 && (
              <span className={styles.lastUpdated}>
                {t(
                  'widget:themeOverview.tabs.earnings.readThrough.basedOnRecaps',
                  { count: recapBasisCount }
                )}
              </span>
            )}
          </div>
          <Separator className={styles.separator} />
        </>
      )}
      <div className={styles.topicsSection}>
        <p className={styles.sectionTitle}>
          {t('widget:themeOverview.tabs.earnings.readThrough.topDrivers')}
        </p>
        {hasDrivers ? (
          <p className={styles.topics}>
            {drivers.map((driver, index) => {
              const themeId = driver.themeId;
              return (
                <Fragment
                  key={themeId ?? `${driver.themeType}:${driver.label}`}
                >
                  {index > 0 && (
                    <span className={styles.topicSeparator} aria-hidden="true">
                      {' · '}
                    </span>
                  )}
                  {themeId ? (
                    <span
                      className={clsx(
                        styles.topic,
                        styles.topicButton,
                        THEME_TAG_COLOR_CLASS_MAP[driver.themeType]
                      )}
                      onClick={() => onTopicClick(themeId)}
                    >
                      <span className={styles.topicLabel}>{driver.label}</span>
                    </span>
                  ) : (
                    <span
                      className={clsx(
                        styles.topic,
                        THEME_TAG_COLOR_CLASS_MAP[driver.themeType]
                      )}
                    >
                      {driver.label}
                    </span>
                  )}
                </Fragment>
              );
            })}
          </p>
        ) : (
          hasReporters && (
            <p className={styles.summary}>
              {t(
                'widget:themeOverview.tabs.earnings.readThrough.topDriversDescription'
              )}
            </p>
          )
        )}
      </div>
    </Card>
  );
};
